/**
 * THE single source of truth for channel descriptors — consumed by BOTH
 * frontends (`landing/src/channels.ts` and `dashboard/src/channels.ts`
 * are thin re-exports). Mirrors the backend `ChannelRegistry`.
 *
 * Before this file existed the two apps kept divergent copies, which is
 * how nostr/irc ended up "coming soon" on the landing yet selectable as
 * sign-in tabs in the dashboard. One file, one flag, no divergence.
 *
 * Field semantics:
 *  - `available`      — live end-to-end RIGHT NOW (code shipped AND
 *    operator setup done; see PENDING_CHANNEL_SETUP.md). Hidden from
 *    every user-facing surface when false.
 *  - `loginSupported` — the backend channel implements
 *    `login_instructions`, so a dashboard sign-in can complete. Matrix
 *    delivers messages but has no inbound webhook → false.
 *
 * Kept in sync with the backend manually rather than fetched at
 * runtime — the descriptor is essentially static and fetching would add
 * a round-trip on every page load.
 */

/** Wire-form channel kind (matches backend `Channel::kind()`). */
export type ChannelKind = string;

/** How an inbound message reaches us: native webhook vs sync-on-poll. */
export type ReceiveMode = 'push' | 'pull';

/** Verification mechanism class — drives the "waiting for…" UI. */
export type VerificationFlavour = 'link-click' | 'pending-link';

export interface ChannelDescriptor {
  readonly kind: ChannelKind;
  /** Title-case label for tabs, table headers, copy. */
  readonly displayName: string;
  /** Live end-to-end right now. Hidden from all user surfaces when false. */
  readonly available: boolean;
  /** Backend can complete a bot-side dashboard sign-in. */
  readonly loginSupported: boolean;

  // ---- landing presentation ----
  /** Brand-style label used in headlines, e.g. "Telegram", "email". */
  readonly headlineLabel: string;
  /** Short fragment for the "shared bot" pricing line. */
  readonly sharedBotLabel?: string;
  /** Short fragment for the "custom bot" paid feature. */
  readonly customLabel?: string;
  /** How owner-sent messages reach our inbox. Ignored when unavailable. */
  readonly receive: ReceiveMode;
  /** Brand colour for the logo tile (CSS hex). */
  readonly brandColor: string;
  /** Simple-icons SVG path (24x24 viewbox); letter mark fallback below. */
  readonly logoPath?: string;
  /** Letter-mark fallback when `logoPath` is missing. */
  readonly letterMark?: string;

  // ---- dashboard sign-in ----
  /** Label above the address input on the sign-in tab. */
  readonly addressLabel: string;
  readonly addressPlaceholder: string;
  readonly addressInputType: 'email' | 'text';
  /** Button label on the sign-in tab. */
  readonly signInCta: string;
  /** H1 shown on the "waiting for…" page once login_start succeeds. */
  readonly waitingTitle: string;
  /** `pending-link` triggers polling, `link-click` doesn't. */
  readonly flavour: VerificationFlavour;
  /** True if /v1/auth/login_start requires `address` for this kind. */
  readonly loginAddressRequired: boolean;
}

export const CHANNELS: ChannelDescriptor[] = [
  {
    kind: 'email',
    displayName: 'Email',
    available: true,
    loginSupported: true,
    headlineLabel: 'email',
    sharedBotLabel: 'ai@vocalcord.io',
    customLabel: 'Custom email From-name + subject',
    receive: 'push',
    brandColor: '#ea4335',
    logoPath: 'M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z',
    addressLabel: 'Email address',
    addressPlaceholder: 'you@example.com',
    addressInputType: 'email',
    signInCta: 'Send sign-in link',
    waitingTitle: 'Check your email',
    flavour: 'link-click',
    loginAddressRequired: true,
  },
  {
    kind: 'telegram',
    displayName: 'Telegram',
    available: true,
    loginSupported: true,
    headlineLabel: 'Telegram',
    sharedBotLabel: '@vocalcord_bot',
    customLabel: 'Custom Telegram bot',
    receive: 'push',
    brandColor: '#229ED9',
    logoPath: 'M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z',
    addressLabel: '@handle',
    addressPlaceholder: '@yourhandle',
    addressInputType: 'text',
    signInCta: 'Get Telegram link',
    waitingTitle: 'Open Telegram',
    flavour: 'pending-link',
    loginAddressRequired: false,
  },
  {
    kind: 'slack',
    displayName: 'Slack',
    available: true,
    loginSupported: true,
    headlineLabel: 'Slack',
    sharedBotLabel: '@vocalcord (shared workspace)',
    customLabel: 'Custom Slack workspace install',
    receive: 'push',
    brandColor: '#4A154B',
    logoPath: 'M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z',
    addressLabel: 'Slack username',
    addressPlaceholder: 'your-slack-handle',
    addressInputType: 'text',
    signInCta: 'Get Slack link',
    waitingTitle: 'Open Slack',
    flavour: 'pending-link',
    loginAddressRequired: false,
  },
  {
    kind: 'discord',
    displayName: 'Discord',
    available: true,
    loginSupported: true,
    headlineLabel: 'Discord',
    sharedBotLabel: '@VocalCord bot',
    customLabel: 'Custom Discord bot',
    receive: 'pull',
    brandColor: '#5865F2',
    logoPath: 'M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z',
    addressLabel: 'Discord username',
    addressPlaceholder: 'your_discord_handle',
    addressInputType: 'text',
    signInCta: 'Get Discord link',
    waitingTitle: 'Open Discord',
    flavour: 'pending-link',
    loginAddressRequired: false,
  },
  {
    kind: 'line',
    displayName: 'LINE',
    available: true,
    loginSupported: true,
    headlineLabel: 'LINE',
    sharedBotLabel: '@vocalcord LINE OA',
    customLabel: 'Custom LINE channel',
    receive: 'push',
    brandColor: '#06C755',
    logoPath: 'M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314',
    addressLabel: 'LINE display name',
    addressPlaceholder: 'your-line-name',
    addressInputType: 'text',
    signInCta: 'Get LINE link',
    waitingTitle: 'Open LINE',
    flavour: 'pending-link',
    loginAddressRequired: false,
  },
  {
    kind: 'gchat',
    displayName: 'Google Chat',
    available: false,
    loginSupported: true,
    headlineLabel: 'Google Chat',
    sharedBotLabel: 'VocalCord Workspace app',
    customLabel: 'Custom Workspace deployment',
    receive: 'push',
    brandColor: '#00897B',
    logoPath: 'M1.637 0C.733 0 0 .733 0 1.637v16.5c0 .904.733 1.636 1.637 1.636h3.955v3.323c0 .804.97 1.207 1.539.638l3.963-3.96h11.27c.903 0 1.636-.733 1.636-1.637V5.592L18.408 0Zm3.955 5.592h12.816v8.59H8.455l-2.863 2.863Z',
    addressLabel: 'Workspace email',
    addressPlaceholder: 'you@yourcompany.com',
    addressInputType: 'email',
    signInCta: 'Get Google Chat link',
    waitingTitle: 'Open Google Chat',
    flavour: 'pending-link',
    loginAddressRequired: false,
  },
  {
    kind: 'twitch',
    displayName: 'Twitch',
    available: false,
    loginSupported: true,
    headlineLabel: 'Twitch chat',
    customLabel: 'Per-broadcaster Twitch bot',
    receive: 'push',
    brandColor: '#9146FF',
    logoPath: 'M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z',
    addressLabel: 'Twitch login',
    addressPlaceholder: 'your_twitch_login',
    addressInputType: 'text',
    signInCta: 'Get Twitch link',
    waitingTitle: 'Open Twitch chat',
    flavour: 'pending-link',
    loginAddressRequired: false,
  },
  {
    kind: 'gitlab',
    displayName: 'GitLab',
    available: false,
    loginSupported: true,
    headlineLabel: 'GitLab',
    customLabel: 'GitLab issue / MR @-mention summoning',
    receive: 'push',
    brandColor: '#FC6D26',
    logoPath: 'm23.6004 9.5927-.0337-.0862L20.3.9814a.851.851 0 0 0-.3362-.405.8748.8748 0 0 0-.9997.0539.8748.8748 0 0 0-.29.4399l-2.2055 6.748H7.5375l-2.2057-6.748a.8573.8573 0 0 0-.29-.4412.8748.8748 0 0 0-.9997-.0537.8585.8585 0 0 0-.3362.4049L.4332 9.5015l-.0325.0862a6.0657 6.0657 0 0 0 2.0119 7.0105l.0113.0087.03.0213 4.976 3.7264 2.462 1.8633 1.4995 1.1321a1.0085 1.0085 0 0 0 1.2197 0l1.4995-1.1321 2.4619-1.8633 5.006-3.7489.0125-.01a6.0682 6.0682 0 0 0 2.0094-7.003z',
    addressLabel: 'GitLab username',
    addressPlaceholder: '@your-gitlab-handle',
    addressInputType: 'text',
    signInCta: 'Get GitLab link',
    waitingTitle: 'Open GitLab',
    flavour: 'pending-link',
    loginAddressRequired: false,
  },
  {
    kind: 'mattermost',
    displayName: 'Mattermost',
    available: false,
    loginSupported: true,
    headlineLabel: 'Mattermost',
    customLabel: 'Self-hosted Mattermost bridge',
    receive: 'push',
    brandColor: '#0058CC',
    logoPath: 'M12.081 0C7.048-.034 2.339 3.125.637 8.153c-2.125 6.276 1.24 13.086 7.516 15.21 6.276 2.125 13.086-1.24 15.21-7.516 1.727-5.1-.172-10.552-4.311-13.557l.126 2.547c2.065 2.282 2.88 5.512 1.852 8.549-1.534 4.532-6.594 6.915-11.3 5.321-4.708-1.593-7.28-6.559-5.745-11.092 1.031-3.046 3.655-5.121 6.694-5.67l1.642-1.94A4.87 4.87 0 0 0 12.08 0zm3.528 1.094a.284.284 0 0 0-.123.024l-.004.001a.33.33 0 0 0-.109.071c-.145.142-.657.828-.657.828L13.6 3.4l-1.3 1.585-2.232 2.776s-1.024 1.278-.798 2.851c.226 1.574 1.396 2.34 2.304 2.648.907.307 2.302.408 3.438-.704 1.135-1.112 1.098-2.75 1.098-2.75l-.087-3.56-.07-2.05-.047-1.775s.01-.856-.02-1.057a.33.33 0 0 0-.035-.107l-.006-.012-.007-.011a.277.277 0 0 0-.229-.14z',
    addressLabel: 'Mattermost username',
    addressPlaceholder: 'your-handle',
    addressInputType: 'text',
    signInCta: 'Get Mattermost link',
    waitingTitle: 'Open Mattermost',
    flavour: 'pending-link',
    loginAddressRequired: false,
  },
  {
    kind: 'zalo',
    displayName: 'Zalo',
    available: false,
    loginSupported: true,
    headlineLabel: 'Zalo',
    customLabel: 'Zalo Official Account',
    receive: 'push',
    brandColor: '#0068FF',
    logoPath: 'M12.49 10.2722v-.4496h1.3467v6.3218h-.7704a.576.576 0 01-.5763-.5729l-.0006.0005a3.273 3.273 0 01-1.9372.6321c-1.8138 0-3.2844-1.4697-3.2844-3.2823 0-1.8125 1.4706-3.2822 3.2844-3.2822a3.273 3.273 0 011.9372.6321l.0006.0005zM6.9188 7.7896v.205c0 .3823-.051.6944-.2995 1.0605l-.03.0343c-.0542.0615-.1815.206-.2421.2843L2.024 14.8h4.8948v.7682a.5764.5764 0 01-.5767.5761H0v-.3622c0-.4436.1102-.6414.2495-.8476L4.8582 9.23H.1922V7.7896h6.7266zm8.5513 8.3548a.4805.4805 0 01-.4803-.4798v-7.875h1.4416v8.3548H15.47zM20.6934 9.6C22.52 9.6 24 11.0807 24 12.9044c0 1.8252-1.4801 3.306-3.3066 3.306-1.8264 0-3.3066-1.4808-3.3066-3.306 0-1.8237 1.4802-3.3044 3.3066-3.3044zm-10.1412 5.253c1.0675 0 1.9324-.8645 1.9324-1.9312 0-1.065-.865-1.9295-1.9324-1.9295s-1.9324.8644-1.9324 1.9295c0 1.0667.865 1.9312 1.9324 1.9312zm10.1412 0c1.0712 0 1.9359-.8645 1.9359-1.9312 0-1.065-.8647-1.9295-1.9359-1.9295-1.0675 0-1.9323.8644-1.9323 1.9295 0 1.0667.8648 1.9312 1.9323 1.9312z',
    addressLabel: 'Zalo display name',
    addressPlaceholder: 'your name',
    addressInputType: 'text',
    signInCta: 'Get Zalo link',
    waitingTitle: 'Open Zalo',
    flavour: 'pending-link',
    loginAddressRequired: false,
  },
  {
    kind: 'feishu',
    displayName: 'Feishu',
    available: false,
    loginSupported: true,
    headlineLabel: 'Feishu / Lark',
    customLabel: 'Lark workspace bot',
    receive: 'push',
    brandColor: '#00D6B9',
    letterMark: 'F',
    addressLabel: 'Workspace email',
    addressPlaceholder: 'you@yourcompany.com',
    addressInputType: 'email',
    signInCta: 'Get Feishu link',
    waitingTitle: 'Open Feishu',
    flavour: 'pending-link',
    loginAddressRequired: false,
  },
  {
    kind: 'wechat',
    displayName: 'WeChat',
    available: false,
    loginSupported: true,
    headlineLabel: 'WeChat',
    customLabel: 'WeChat Official Account',
    receive: 'push',
    brandColor: '#07C160',
    logoPath: 'M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 0 0 .167-.054l1.903-1.114a.864.864 0 0 1 .717-.098 10.16 10.16 0 0 0 2.837.403c.276 0 .543-.027.811-.05-.857-2.578.157-4.972 1.932-6.446 1.703-1.415 3.882-1.98 5.853-1.838-.576-3.583-4.196-6.348-8.596-6.348zM5.785 5.991c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178A1.17 1.17 0 0 1 4.623 7.17c0-.651.52-1.18 1.162-1.18zm5.813 0c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178 1.17 1.17 0 0 1-1.162-1.178c0-.651.52-1.18 1.162-1.18zm5.34 2.867c-1.797-.052-3.746.512-5.28 1.786-1.72 1.428-2.687 3.72-1.78 6.22.942 2.453 3.666 4.229 6.884 4.229.826 0 1.622-.12 2.361-.336a.722.722 0 0 1 .598.082l1.584.926a.272.272 0 0 0 .14.047c.134 0 .24-.111.24-.247 0-.06-.023-.12-.038-.177l-.327-1.233a.582.582 0 0 1-.023-.156.49.49 0 0 1 .201-.398C23.024 18.48 24 16.82 24 14.98c0-3.21-2.931-5.837-6.656-6.088V8.89c-.135-.01-.27-.027-.407-.03zm-2.53 3.274c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.97-.982zm4.844 0c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.969-.982z',
    addressLabel: 'WeChat name',
    addressPlaceholder: 'your name',
    addressInputType: 'text',
    signInCta: 'Get WeChat link',
    waitingTitle: 'Open WeChat',
    flavour: 'pending-link',
    loginAddressRequired: false,
  },
  {
    kind: 'github',
    displayName: 'GitHub',
    available: true,
    loginSupported: true,
    headlineLabel: 'GitHub',
    customLabel: '@VocalCord on issues + PRs',
    receive: 'push',
    brandColor: '#181717',
    logoPath: 'M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12',
    addressLabel: 'GitHub username',
    addressPlaceholder: '@your-github-handle',
    addressInputType: 'text',
    signInCta: 'Get GitHub link',
    waitingTitle: 'Mention @VocalCord on GitHub',
    flavour: 'pending-link',
    loginAddressRequired: false,
  },
  {
    kind: 'jira',
    displayName: 'Jira',
    available: false,
    loginSupported: true,
    headlineLabel: 'Jira',
    customLabel: '@VocalCord on Jira issues',
    receive: 'push',
    brandColor: '#0052CC',
    logoPath: 'M11.571 11.513H0a5.218 5.218 0 0 0 5.232 5.215h2.13v2.057A5.215 5.215 0 0 0 12.575 24V12.518a1.005 1.005 0 0 0-1.005-1.005zm5.723-5.756H5.736a5.215 5.215 0 0 0 5.215 5.214h2.129v2.058a5.218 5.218 0 0 0 5.215 5.214V6.758a1.001 1.001 0 0 0-1.001-1.001zM23.013 0H11.455a5.215 5.215 0 0 0 5.215 5.215h2.129v2.057A5.215 5.215 0 0 0 24 12.483V1.005A1.001 1.001 0 0 0 23.013 0Z',
    addressLabel: 'Jira display name',
    addressPlaceholder: 'your name',
    addressInputType: 'text',
    signInCta: 'Get Jira link',
    waitingTitle: 'Mention @VocalCord on Jira',
    flavour: 'pending-link',
    loginAddressRequired: false,
  },
  {
    kind: 'msteams',
    displayName: 'Microsoft Teams',
    available: false,
    loginSupported: true,
    headlineLabel: 'Teams',
    customLabel: 'Teams app per tenant',
    receive: 'push',
    brandColor: '#6264A7',
    logoPath: 'M20.625 8.127q-.55 0-1.025-.205-.475-.205-.832-.563-.358-.357-.563-.832Q18 6.053 18 5.502q0-.54.205-1.02t.563-.837q.357-.358.832-.563.474-.205 1.025-.205.54 0 1.02.205t.837.563q.358.357.563.837.205.48.205 1.02 0 .55-.205 1.025-.205.475-.563.832-.357.358-.837.563-.48.205-1.02.205zm0-3.75q-.469 0-.797.328-.328.328-.328.797 0 .469.328.797.328.328.797.328.469 0 .797-.328.328-.328.328-.797 0-.469-.328-.797-.328-.328-.797-.328zM24 10.002v5.578q0 .774-.293 1.46-.293.685-.803 1.194-.51.51-1.195.803-.686.293-1.459.293-.445 0-.908-.105-.463-.106-.85-.329-.293.95-.855 1.729-.563.78-1.319 1.336-.756.557-1.67.861-.914.305-1.898.305-1.148 0-2.162-.398-1.014-.399-1.805-1.102-.79-.703-1.312-1.664t-.674-2.086h-5.8q-.411 0-.704-.293T0 16.881V6.873q0-.41.293-.703t.703-.293h8.59q-.34-.715-.34-1.5 0-.727.275-1.365.276-.639.75-1.114.475-.474 1.114-.75.638-.275 1.365-.275t1.365.275q.639.276 1.114.75.474.475.75 1.114.275.638.275 1.365t-.275 1.365q-.276.639-.75 1.113-.475.475-1.114.75-.638.276-1.365.276-.188 0-.375-.024-.188-.023-.375-.058v1.078h10.875q.469 0 .797.328.328.328.328.797zM12.75 2.373q-.41 0-.78.158-.368.158-.638.434-.27.275-.428.639-.158.363-.158.773 0 .41.158.78.159.368.428.638.27.27.639.428.369.158.779.158.41 0 .773-.158.364-.159.64-.428.274-.27.433-.639.158-.369.158-.779 0-.41-.158-.773-.159-.364-.434-.64-.275-.275-.639-.433-.363-.158-.773-.158zM6.937 9.814h2.25V7.94H2.814v1.875h2.25v6h1.875zm10.313 7.313v-6.75H12v6.504q0 .41-.293.703t-.703.293H8.309q.152.809.556 1.5.405.691.985 1.19.58.497 1.318.779.738.281 1.582.281.926 0 1.746-.352.82-.351 1.436-.966.615-.616.966-1.43.352-.815.352-1.752zm5.25-1.547v-5.203h-3.75v6.855q.305.305.691.452.387.146.809.146.469 0 .879-.176.41-.175.715-.48.304-.305.48-.715t.176-.879Z',
    addressLabel: 'Teams display name',
    addressPlaceholder: 'your name',
    addressInputType: 'text',
    signInCta: 'Get Teams link',
    waitingTitle: 'Open Microsoft Teams',
    flavour: 'pending-link',
    loginAddressRequired: false,
  },
  {
    kind: 'matrix',
    displayName: 'Matrix',
    available: true,
    loginSupported: false,
    headlineLabel: 'Matrix',
    sharedBotLabel: '@vocalcord:matrix.vocalcord.io',
    customLabel: 'BYO homeserver',
    receive: 'pull',
    brandColor: '#000000',
    logoPath: 'M.632.55v22.9H2.28V24H0V0h2.28v.55zm7.043 7.26v1.157h.033c.309-.443.683-.784 1.117-1.024.433-.245.936-.365 1.5-.365.54 0 1.033.107 1.481.314.448.208.785.582 1.02 1.108.254-.374.6-.706 1.034-.992.434-.287.95-.43 1.546-.43.453 0 .872.056 1.26.167.388.11.716.286.993.53.276.245.489.559.646.951.152.392.23.863.23 1.417v5.728h-2.349V11.52c0-.286-.01-.559-.032-.812a1.755 1.755 0 0 0-.18-.66 1.106 1.106 0 0 0-.438-.448c-.194-.11-.457-.166-.785-.166-.332 0-.6.064-.803.189a1.38 1.38 0 0 0-.48.499 1.946 1.946 0 0 0-.231.696 5.56 5.56 0 0 0-.06.785v4.768h-2.35v-4.8c0-.254-.004-.503-.018-.752a2.074 2.074 0 0 0-.143-.688 1.052 1.052 0 0 0-.415-.503c-.194-.125-.476-.19-.854-.19-.111 0-.259.024-.439.074-.18.051-.36.143-.53.282-.171.138-.319.337-.439.595-.12.259-.18.6-.18 1.02v4.966H5.46V7.81zm15.693 15.64V.55H21.72V0H24v24h-2.28v-.55z',
    addressLabel: 'Matrix mxid',
    addressPlaceholder: '@you:matrix.org',
    addressInputType: 'text',
    signInCta: 'Get Matrix link',
    waitingTitle: 'Open Matrix',
    flavour: 'pending-link',
    loginAddressRequired: false,
  },
  {
    kind: 'signal',
    displayName: 'Signal',
    available: false,
    loginSupported: true,
    headlineLabel: 'Signal',
    customLabel: 'Signal via signal-cli',
    receive: 'pull',
    brandColor: '#3A76F0',
    logoPath: 'M12 0q-.934 0-1.83.139l.17 1.111a11 11 0 0 1 3.32 0l.172-1.111A12 12 0 0 0 12 0M9.152.34A12 12 0 0 0 5.77 1.742l.584.961a10.8 10.8 0 0 1 3.066-1.27zm5.696 0-.268 1.094a10.8 10.8 0 0 1 3.066 1.27l.584-.962A12 12 0 0 0 14.848.34M12 2.25a9.75 9.75 0 0 0-8.539 14.459c.074.134.1.292.064.441l-1.013 4.338 4.338-1.013a.62.62 0 0 1 .441.064A9.7 9.7 0 0 0 12 21.75c5.385 0 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25m-7.092.068a12 12 0 0 0-2.59 2.59l.909.664a11 11 0 0 1 2.345-2.345zm14.184 0-.664.909a11 11 0 0 1 2.345 2.345l.909-.664a12 12 0 0 0-2.59-2.59',
    addressLabel: 'Phone number',
    addressPlaceholder: '+1…',
    addressInputType: 'text',
    signInCta: 'Get Signal link',
    waitingTitle: 'Open Signal',
    flavour: 'pending-link',
    loginAddressRequired: false,
  },
  {
    kind: 'urbit',
    displayName: 'Urbit / Tlon',
    available: false,
    loginSupported: true,
    headlineLabel: 'Tlon',
    customLabel: 'Per-ship Urbit poke',
    receive: 'pull',
    brandColor: '#000000',
    letterMark: '~',
    addressLabel: 'Urbit ship',
    addressPlaceholder: '~sampel-palnet',
    addressInputType: 'text',
    signInCta: 'Get Urbit link',
    waitingTitle: 'Open Tlon',
    flavour: 'pending-link',
    loginAddressRequired: false,
  },
  {
    kind: 'nostr',
    displayName: 'Nostr',
    available: false,
    loginSupported: true,
    headlineLabel: 'Nostr',
    customLabel: 'Encrypted relay DMs',
    receive: 'pull',
    brandColor: '#8E30EB',
    letterMark: 'N',
    addressLabel: 'npub',
    addressPlaceholder: 'npub1…',
    addressInputType: 'text',
    signInCta: 'Get Nostr link',
    waitingTitle: 'Open your Nostr client',
    flavour: 'pending-link',
    loginAddressRequired: false,
  },
  {
    kind: 'irc',
    displayName: 'IRC',
    available: false,
    loginSupported: true,
    headlineLabel: 'IRC',
    customLabel: 'Per-network IRC bridge',
    receive: 'pull',
    brandColor: '#3F3F3F',
    letterMark: 'IRC',
    addressLabel: 'IRC nick',
    addressPlaceholder: 'yournick',
    addressInputType: 'text',
    signInCta: 'Get IRC link',
    waitingTitle: 'Open IRC client',
    flavour: 'pending-link',
    loginAddressRequired: false,
  },
];

/** Display name for a kind, falling back to the raw kind string so an
 * unknown backend channel never crashes the UI. */
export function getChannelDisplayName(kind: ChannelKind): string {
  return CHANNELS.find((c) => c.kind === kind)?.displayName ?? kind;
}

/** Comma-separated list of AVAILABLE headline labels — tagline copy. */
export function headlineList(): string {
  return CHANNELS.filter((c) => c.available)
    .map((c) => c.headlineLabel)
    .join(', ');
}

/**
 * Glyph fill for a brand colour: near-black brands (GitHub #181717,
 * Matrix #000) vanish on the dark theme, so lift those to the page
 * foreground colour. Returns either the brand hex or `var(--fg)`.
 */
export function glyphColorFor(brandHex: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(brandHex);
  if (!m) return brandHex;
  const v = parseInt(m[1], 16);
  const lum =
    0.2126 * ((v >> 16) & 0xff) + 0.7152 * ((v >> 8) & 0xff) + 0.0722 * (v & 0xff);
  return lum < 40 ? 'var(--fg)' : brandHex;
}
